import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  PagedResultDto,
  Timesheet,
  TimesheetRequest,
  TimesheetWithEntry
} from '../../models';
import { HttpHelper } from '@umss/shared/utils';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TimesheetService {
  private readonly baseUrl = '/api/Timesheets';

  constructor(private http: HttpClient) {}

  /**
   * Fetch timesheets for a facility, with optional filters.
   * @param facilityId The ID of the facility.
   * @param params Optional filters like date range.
   * @returns An Observable array of Timesheets.
   */
  getAll(
    payload?: Partial<TimesheetRequest>
  ): Observable<PagedResultDto<Timesheet>> {
    const params = HttpHelper.toHttpParams(payload ?? {});
    return this.http.get<PagedResultDto<Timesheet>>(this.baseUrl, { params });
  }

  getEntries(
    payload?: Partial<TimesheetRequest>
  ): Observable<PagedResultDto<TimesheetWithEntry>> {
    const params = HttpHelper.toHttpParams(payload ?? {});
    return this.http.get<PagedResultDto<TimesheetWithEntry>>(
      `${this.baseUrl}/entries`,
      {
        params
      }
    );
  }

  /**
   * Get a single timesheet entry by its ID.
   * @param entryId The ID of the entry (not timesheetId).
   * @returns An Observable of the TimesheetWithEntry.
   */
  getEntry(entryId: string): Observable<TimesheetWithEntry> {
    return this.http.get<TimesheetWithEntry>(
      `${this.baseUrl}/entries/${entryId}`
    );
  }

  /**
   * Get a single timesheet by its ID.
   * @param facilityId The ID of the facility.
   * @param timesheetId The ID of the timesheet.
   * @returns An Observable of the Timesheet.
   */
  get(id: string): Observable<Timesheet> {
    return this.http.get<Timesheet>(`${this.baseUrl}/${id}`);
  }

  getPending(facilityId: string): Observable<Timesheet[]> {
    const params = HttpHelper.toHttpParams({ facilityId });
    return this.http.get<Timesheet[]>(`${this.baseUrl}/${facilityId}`, {
      params
    });
  }

  /**
   * Approve a timesheet.
   * @param timesheetId The ID of the timesheet to approve.
   * @returns An Observable that completes when the operation is done.
   */
  approve(timesheetId: string, comments?: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${timesheetId}/approve`, {
      timesheetId,
      comments
    });
  }

  /**
   * Reject a timesheet.
   * @param timesheetId The ID of the timesheet to reject.
   * @param reason The reason for rejection.
   * @returns An Observable that completes when the operation is done.
   */
  reject(timesheetId: string, rejectionReason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${timesheetId}/reject`, {
      rejectionReason,
      timesheetId
    });
  }

  submit(facilityId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${facilityId}/reject`, {});
  }

  generate(weekStartDate: string): Observable<Timesheet> {
    return this.http.post<Timesheet>(`${this.baseUrl}/generate`, {
      weekStartDate
    });
  }
}
